import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Card, CardContent, PageSkeleton } from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { MnoIpRangeForm } from '@/components/features/mno-ip-ranges/mno-ip-range-form';
import {
  useMnoIpRange,
  useMnoOperators,
  useUpdateMnoIpRange,
} from '@/hooks/use-mno-ip-ranges';
import { ApiError } from '@/lib/api/client';
import type {
  CreateMnoIpRangeInput,
  MnoIpRangeEntry,
  UpdateMnoIpRangeInput,
} from '@/types/mno-ip-range';

function sameCidrs(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((c, i) => c === b[i]);
}

/**
 * Baut ein partielles Update: cidrs nur bei Aenderung (Replace, sonst Metadaten-
 * only), status immer (Pflicht — haelt das PUT nicht-leer), label/description mit
 * Clear-Semantik (leer→null, geaendert→Wert, unveraendert→weglassen). Spec 326 §2/§4.
 */
function buildUpdate(input: CreateMnoIpRangeInput, entry: MnoIpRangeEntry): UpdateMnoIpRangeInput {
  const update: UpdateMnoIpRangeInput = {
    updatedAt: entry.updatedAt,
    status: input.status ?? entry.status,
  };

  const oldCidrs = entry.ipRanges.map((r) => r.cidr);
  if (!sameCidrs(input.cidrs, oldCidrs)) update.cidrs = input.cidrs;

  applyNullable(update, 'label', input.label, entry.label);
  applyNullable(update, 'description', input.description, entry.description);
  return update;
}

/** undefined(leer) vs vorher: leer→null wenn vorher Wert; geaendert→Wert; sonst weglassen. */
function applyNullable(
  update: UpdateMnoIpRangeInput,
  key: 'label' | 'description',
  next: string | undefined,
  old: string | undefined,
): void {
  const oldVal = old ?? undefined;
  if (next === undefined) {
    if (oldVal !== undefined) update[key] = null; // geleert
    return; // unveraendert (beide leer)
  }
  if (next !== oldVal) update[key] = next; // geaendert
}

export function MnoIpRangeEditPage() {
  const { t } = useTranslation('mno-ip-ranges');
  const navigate = useNavigate();
  const { operatorId, countryCode } = useParams<{ operatorId: string; countryCode: string }>();
  const { data: entry, isLoading, error, refetch } = useMnoIpRange(operatorId, countryCode);
  const { data: operators } = useMnoOperators();
  const updateMutation = useUpdateMnoIpRange(operatorId ?? '', countryCode ?? '');
  // Optimistic-Lock-Konflikt (409): Banner + neu geladen, bewusstes erneutes Speichern.
  const [conflict, setConflict] = useState<string | null>(null);
  // Server-Validierungsfehler (400/422) — formularnah.
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 404 beim Detail-GET (zwischenzeitlich geloescht) → Toast + zurueck zur Liste.
  useEffect(() => {
    if (error instanceof ApiError && error.status === 404) {
      toast.error(t('edit.notFound'));
      navigate('/mno-ip-ranges');
    }
  }, [error, navigate, t]);

  const handleSubmit = (input: CreateMnoIpRangeInput) => {
    if (!entry) return; // erst nach erfolgreichem GET aufrufbar (Form rendert dann)
    setSubmitError(null);
    updateMutation.mutate(buildUpdate(input, entry), {
      onSuccess: () => navigate('/mno-ip-ranges'),
      onError: (err) => {
        if (err instanceof ApiError && err.status === 404) {
          toast.error(t('edit.notFound'));
          navigate('/mno-ip-ranges');
          return;
        }
        if (err instanceof ApiError && err.status === 409) {
          // Kein stilles Auto-Retry: aktuellen Stand neu laden (Form rehydriert via
          // key), Konflikt anzeigen; der Nutzer prueft und speichert bewusst erneut.
          setConflict(err.message || t('edit.conflict'));
          void refetch();
          return;
        }
        if (err instanceof ApiError && (err.status === 400 || err.status === 422)) {
          setSubmitError(err.message || t('edit.saveFailed'));
          return;
        }
        toast.error(err instanceof Error ? err.message : t('edit.saveFailed'));
      },
    });
  };

  // ApiErrorDisplay nur fuer Nicht-404-Fehler (404 wird oben weggeleitet).
  const showErrorDisplay = !!error && !(error instanceof ApiError && error.status === 404);

  return (
    <div>
      <PageHeader title={t('edit.title')} description={t('edit.description')} />

      {isLoading && <PageSkeleton />}
      {showErrorDisplay && <ApiErrorDisplay error={error} />}

      {!isLoading && !error && entry && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            {conflict && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-medium">{t('edit.conflictTitle')}</p>
                  <p>{conflict}</p>
                </div>
              </div>
            )}
            <MnoIpRangeForm
              // Rehydriert die Form nach 409-Reload auf den frischen Serverstand.
              key={entry.updatedAt}
              mode="edit"
              operators={operators ?? []}
              initial={entry}
              submitting={updateMutation.isPending}
              submitError={submitError}
              onSubmit={handleSubmit}
              onCancel={() => navigate('/mno-ip-ranges')}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
